export interface ChatContact {
  uniqueUserId: string;
  firstName: string;
  lastName: string;
  photo: string | null;
  lastMessage: string | null;
  lastMessageDate: string | null;
  unreadCount: number;
}

export interface ChatMessage {
  uniqueStudyId: string;
  uniqueChatMessageId: string;
  senderUniqueUserId: string;
  senderFirstName: string;
  senderLastName: string;
  recipientUniqueUserId: string;
  studyName?: string | null;
  messageText: string;
  sentDate: string;
  readDate: string | null;
  isDeleted?: boolean;
}

export interface ChatInteraction {
  firstUniqueUserId: string;
  firstFirstName: string;
  firstLastName: string;
  secondUniqueUserId: string;
  secondFirstName: string;
  secondLastName: string;
  lastMessage: string | null;
  lastMessageDate: string | null;
  lastMessageIsDeleted: boolean;
}

export interface ChatThread {
  messages: ChatMessage[];
  hasOlder: boolean;
}

export interface ChatPresenceEvent {
  uniqueStudyId?: string;
  uniqueUserId?: string;
  isOnline?: boolean;
  UniqueStudyId?: string;
  UniqueUserId?: string;
  IsOnline?: boolean;
}

export interface ChatMessagesReadEvent {
  uniqueStudyId: string;
  readerUniqueUserId: string;
  otherUniqueUserId: string;
}

export interface ChatMessageDeletedEvent {
  uniqueStudyId: string;
  uniqueChatMessageId: string;
  senderUniqueUserId: string;
  recipientUniqueUserId: string;
}

export interface ChatThreadEntry {
  kind: 'day' | 'message';
  trackId: string;
  label?: string;
  message?: ChatMessage;
}

const AVATAR_COLORS = ['#2563eb', '#10b981', '#7c3aed', '#f59e0b', '#0891b2', '#ec4899', '#64748b'];

export function chatId(value: string | null | undefined): string {
  return (value ?? '').toLowerCase();
}

export function chatName(firstName: string, lastName: string): string {
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
}

export function chatInitials(firstName: string, lastName: string): string {
  const initials = `${(firstName ?? '').trim().charAt(0)}${(lastName ?? '').trim().charAt(0)}`.toUpperCase();
  return initials || '?';
}

export function chatAvatarColor(name: string): string {
  let hash = 0;
  for (const char of name) {
    hash = (hash + char.charCodeAt(0)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash] ?? AVATAR_COLORS[0];
}

export function formatChatDate(value: string | null, style: 'stamp' | 'day' = 'stamp'): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const isToday = date.toDateString() === new Date().toDateString();
  if (style === 'day') {
    return isToday ? 'Today' : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return isToday
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function buildThreadEntries(messages: ChatMessage[]): ChatThreadEntry[] {
  const entries: ChatThreadEntry[] = [];
  let previousDay = '';
  for (const message of messages) {
    const day = new Date(message.sentDate).toDateString();
    if (day !== previousDay) {
      previousDay = day;
      entries.push({
        kind: 'day',
        trackId: `day-${message.uniqueChatMessageId}`,
        label: formatChatDate(message.sentDate, 'day')
      });
    }
    entries.push({
      kind: 'message',
      trackId: message.uniqueChatMessageId,
      message
    });
  }
  return entries;
}
